import { Injectable } from "@angular/core";

import { ActiveUser } from "../../../model/core/ActiveUser";
import { ActiveUserProfilePromiseService } from "shared/auth-services";
import { BranchLookupService } from "../../../lookup/model/impl/BranchLookupService";
import { ClientAccountVisibilityType } from "./ClientAccountVisibilityType";
import { StaticDataPrivatizationType } from "../../../model/privatization/enum/StaticDataPrivatizationType";
import { SystemConfigPromiseService } from "shared/auth-services";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
import { ClientAccountIsTradableService } from "./ClientAccountIsTradableService";

const logger = LoggerService.for(__dirname);

@Injectable()
export class ClientAccountVisibilityService extends ClientAccountIsTradableService {
  private _isReady;

  private user: ActiveUser;
  private isVisibilityClientData: boolean;
  private isUnrestricted: boolean;
  private salesGroupsIds;

  constructor(
    protected branchLookup: BranchLookupService,
    protected systemConfig: SystemConfigPromiseService,
    protected activeUserService: ActiveUserProfilePromiseService,
    protected branchService: BranchLookupService,
  ) {
    super();

    this._isReady = new Promise<boolean>((res) => {
      Promise.all([
        activeUserService.getActiveUserData(),
        this.systemConfig.hasRole("ROLE_ORDER_ENTRY_UNRESTRICTED"),
        this.systemConfig.hasRole("ROLE_VISIBILITY_CLIENT_DATA"),
      ]).then((values: any[]) => {
        this.user = values[0];
        this.isUnrestricted = values[1];
        this.isVisibilityClientData = values[2];
        if (CubiqTS.UserType.SALES === this.user.userTypeId) {
          // check sales group membership for sales
          this.salesGroupsIds = new Set(this.user.salesGroupsIdSet);
        }
        res(true);
      });
    });
  }

  /**
   * an account with aan associated client that is tradable is also tradable
   * @param item
   */
  public updateTradableAccounts(item: CubiqTS.AccountImpl): () => void {
    let isTradable = false;
    let wasTradable = false;

    if (item == null) {
      return;
    }

    if (this.user.tradableAccountCodes.has(item.id)) {
      wasTradable = true;
    }

    if (Array.isArray(item.associatedClients)) {
      isTradable = item.associatedClients.some((client) => {
        if (this.user.tradableClientCodes.has(client)) {
          return true;
        }
      });
    }

    if ((isTradable && wasTradable) || (!isTradable && !wasTradable)) {
      return () => {
        // no-op
      };
    } else if (isTradable) {
      this.user.tradableAccountCodes.add(item.id);
    } else {
      this.user.tradableAccountCodes.delete(item.id);
    }

    this.activeUserService.setActiveUserData(this.user);

    return () => {
      this.accountSubject.next({ isTradable: isTradable, wasTradable: wasTradable, id: item.id });
    };
  }

  /**
   * an account with an associated client that is tradable is also tradable
   * @param item
   */
  public updateTradableClients(item: CubiqTS.ClientImpl): () => void {
    let isTradable = false;
    let wasTradable = false;

    if (item == null) {
      return;
    }

    if (this.user.tradableClientCodes.has(item.clientId)) {
      wasTradable = true;
    }

    if (this.isUnrestricted || this.user.isAdmin()) {
      if (this.branchLookup.hasVisiblePrivateBranchCode(item.branchCd)) {
        isTradable = false;
      } else {
        isTradable = true;
      }
    } else if (this.user.isSales()) {
      isTradable = this.user.salesGroupsIds.includes(item.salesGroupId.toString());
    } else if (this.user.isTrader()) {
      isTradable = this.user.tradableBranchCodes.has(item.branchCd);
    }

    if ((isTradable && wasTradable) || (!isTradable && !wasTradable)) {
      return () => {
        // no-op
      };
    } else if (isTradable) {
      this.user.tradableClientCodes.add(item.clientId);
    } else {
      this.user.tradableClientCodes.delete(item.clientId);
    }

    this.activeUserService.setActiveUserData(this.user);

    return () => {
      this.clientSubject.next({
        isTradable: isTradable,
        wasTradable: wasTradable,
        clientId: item.clientId,
      });
    };
  }

  public isReady(): Promise<boolean> {
    return this._isReady;
  }

  public getVisibilityType(
    item: CubiqTS.AccountImpl | CubiqTS.ClientImpl,
  ): Promise<ClientAccountVisibilityType> {
    return new Promise<ClientAccountVisibilityType>((res, rej) => {
      this._isReady.then(() => {
        res(this._getVisibilityType(item));
      });
    });
  }

  /**
   * Invoke only after is ready is resolved
   * @param item
   */
  public _getVisibilityType(
    item: CubiqTS.AccountImpl | CubiqTS.ClientImpl,
  ): ClientAccountVisibilityType {
    if (item instanceof CubiqTS.AccountImpl) {
      if (item.salesGroupId != null) {
        // break account always visible in flex
        return ClientAccountVisibilityType.Full;
      } else if (StaticDataPrivatizationType.hasPrivatization(item)) {
        return ClientAccountVisibilityType.Privatized;
      } else if (this.user.tradableAccountCodes.has(item.id)) {
        return ClientAccountVisibilityType.Full;
      } else {
        return ClientAccountVisibilityType.Visible;
      }
    } else if (item instanceof CubiqTS.ClientImpl) {
      if (StaticDataPrivatizationType.hasPrivatization(item)) {
        return ClientAccountVisibilityType.Privatized;
      } else if (this.user.tradableClientCodes.has(item.clientId)) {
        return ClientAccountVisibilityType.Full;
      } else {
        return ClientAccountVisibilityType.Visible;
      }
    }

    logger.out.warn(
      `${this.constructor.name}.getVisibilityType %O == ClientAccountVisibilityType.None`,
      item,
    );
    return ClientAccountVisibilityType.None;
  }
}
